import React from 'react';
import { useTranslation } from 'react-i18next';
import { TimeoutReason } from '../../hooks/useSessionTimeout';
import './SessionTimeoutModal.css';

interface SessionTimeoutModalProps {
  open: boolean;
  reason: TimeoutReason | null;
  secondsRemaining: number;
  onExtend: () => void;
  onLogout: () => void;
}

export const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  open,
  reason,
  secondsRemaining,
  onExtend,
  onLogout,
}) => {
  const { t } = useTranslation();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  if (!open || !reason) return null;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await onLogout();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  const getMessage = () => {
    if (reason === 'idle') {
      return t('sessionTimeout.idleMessage');
    }
    return t('sessionTimeout.maxDurationMessage');
  };

  const canExtend = reason === 'idle';

  return (
    <div className="session-timeout-overlay">
      <div className="session-timeout-modal">
        <div className="session-timeout-header">
          <h2>⏱️ {t('sessionTimeout.warning')}</h2>
        </div>

        <div className="session-timeout-content">
          <p className="session-timeout-message">{getMessage()}</p>

          <div className="session-timeout-countdown">
            <span className="session-timeout-timer">{formatTime(secondsRemaining)}</span>
            <span className="session-timeout-timer-label">
              {secondsRemaining === 1 ? t('sessionTimeout.secondRemaining') : t('sessionTimeout.secondsRemaining')}
            </span>
          </div>

          {!canExtend && (
            <p className="session-timeout-info">
              {t('sessionTimeout.securityInfo')}
            </p>
          )}
        </div>

        <div className="session-timeout-actions">
          {canExtend ? (
            <>
              <button
                onClick={onExtend}
                className="session-timeout-button session-timeout-button-primary"
                disabled={isLoggingOut}
              >
                {t('sessionTimeout.continueSession')}
              </button>
              <button
                onClick={handleLogout}
                className="session-timeout-button session-timeout-button-secondary"
                disabled={isLoggingOut}
              >
                {isLoggingOut ? t('sessionTimeout.loggingOut') : t('sessionTimeout.logout')}
              </button>
            </>
          ) : (
            <button
              onClick={handleLogout}
              className="session-timeout-button session-timeout-button-primary"
              disabled={isLoggingOut}
            >
              {isLoggingOut ? t('sessionTimeout.loggingOut') : t('sessionTimeout.logout')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
